import { startTransition, useState, useRef, useCallback } from "react";
import { convertDate } from "@Services/luxonHelper";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";

/**
 * The rule bag `addValidationToForms` (CreateInspectionAndProgress/constants.ts) builds from one
 * question field. The four bounds it copies straight off the question come from the wire schema;
 * `required` / `min|maxLength` / `min|maxRange` are this validator's own names for
 * `isMandatory` / `min|maxFieldLimit`, so they stay declared here.
 */
type FieldRules = Pick<QuestionField, "minFieldRange" | "maxFieldRange" | "minNoOfFiles" | "maxNoOfFiles"> & {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minRange?: number;
  maxRange?: number;
  /** Only the two date-time scopes this validator branches on; the rest are handled elsewhere. */
  selectionScope?: Extract<QuestionField["selectionScope"], "futureDateTime" | "pastDateTime">;
};

/**
 * One entry of the rule bag. `validate` writes the last-seen answer back onto the entry so
 * `validateAll` can re-check every field without the caller re-supplying values.
 */
type StoredFieldRules = FieldRules & { value?: unknown };

/** The whole rule bag, keyed by field name (the `name` passed to `validate`). */
type FieldRulesByName = Record<string, StoredFieldRules>;

const useFormValidator = (initialRules: FieldRulesByName = {}) => {
  const [error, setError] = useState<Record<string, string>>({});
  const rulesRef = useRef<FieldRulesByName>(initialRules);

  const updateRules = (newRules: FieldRulesByName) => {
    rulesRef.current = { ...rulesRef.current, ...newRules };
  };

  /**
   * REPLACE the rule bag (and drop the accumulated errors) instead of merging into it.
   *
   * `updateRules` only ever merges, which is correct while one validator serves one form: the bulk
   * file-upload grid deliberately accumulates row-prefixed keys across several `updateRules` calls.
   * It is wrong when a single validator instance OUTLIVES the entity it was built for. Rule keys are
   * question `_id`s (`addValidationToForms`), so two entities on different templates contribute
   * disjoint keys and nothing ever overwrites the old ones. `validateAll` walks the WHOLE bag, so a
   * stale `required` rule fails validation for a field that is not on screen — and because the field
   * is not rendered, `setFieldError` marks nothing and the user sees a dead button.
   *
   * Callers that reuse one validator across entities must call this when the entity changes.
   */
  // `useCallback` with no deps: the body touches only `rulesRef` (a ref) and `setError` (a stable
  // setter), so one identity for the component's lifetime is correct — and it lets callers list this
  // in an effect's dependency array honestly, instead of suppressing exhaustive-deps because the
  // validator object itself is rebuilt every render.
  const resetRules = useCallback((newRules: FieldRulesByName = {}) => {
    rulesRef.current = newRules;
    startTransition(() => setError({}));
  }, []);

  // Validation errors are non-urgent UI feedback — wrap setError in startTransition
  // so per-keystroke validation doesn't block paint by triggering a sync parent re-render.
  const setFieldError = (fieldName: string, message: string) => {
    startTransition(() => {
      setError((prevState: Record<string, string>) => ({
        ...prevState,
        [fieldName]: message,
      }));
    });
  };

  const clearFieldError = (fieldName: string) => {
    startTransition(() => {
      setError((prevState: Record<string, string>) => ({
        ...prevState,
        [fieldName]: "",
      }));
    });
  };

  const checkRequired = (name: string, value: unknown, rules: FieldRules) => {
    if (!rules.required) return true;

    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmptyValue = !value && value !== 0; // allow 0 as valid

    if (isEmptyArray || isEmptyValue) {
      setFieldError(name, "This field is required");
      return false;
    }
    return true;
  };

  const checkStringBounds = (name: string, value: unknown, rules: FieldRules) => {
    if (typeof value !== "string") return true;

    if (rules.minLength && value.length < rules.minLength) {
      setFieldError(name, `Minimum ${rules.minLength} characters required`);
      return false;
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      setFieldError(name, `Maximum ${rules.maxLength} characters allowed`);
      return false;
    }

    return true;
  };

  const checkNumberBounds = (name: string, value: unknown, rules: FieldRules) => {
    if (typeof value !== "number") return true;

    const num = Number(value);

    if (rules.minRange && num < rules.minRange) {
      setFieldError(name, `Minimum value should be ${rules.minRange}`);
      return false;
    }

    if (rules.maxRange && num > rules.maxRange) {
      setFieldError(name, `Maximum value should be ${rules.maxRange}`);
      return false;
    }

    if (rules.maxFieldRange && num > rules.maxFieldRange) {
      setFieldError(name, `Maximum date can be ${convertDate(rules.maxFieldRange, "dateforProjectDashbaord")}`);
      return false;
    }

    if (rules.minFieldRange && num < rules.minFieldRange) {
      setFieldError(name, `Minimum date can be ${convertDate(rules.minFieldRange, "dateforProjectDashbaord")}`);
      return false;
    }

    const scopeError = getDateTimeScopeError(value, rules.selectionScope);
    if (scopeError) {
      setFieldError(name, scopeError);
      return false;
    }

    return true;
  };

  const getDateTimeScopeError = (value: number, scope?: FieldRules["selectionScope"]) => {
    if (!scope) return "";

    const currentDate = new Date();
    const selectedDate = new Date(value);

    if (scope === "futureDateTime" && selectedDate <= currentDate) {
      return "Please select a future date and time";
    }

    if (scope === "pastDateTime" && selectedDate >= currentDate) {
      return "Please select a past date and time";
    }

    return "";
  };

  const checkFileCounts = (name: string, value: unknown, rules: FieldRules) => {
    // File answers are arrays of attachment metadata, but this runs for every field type, so the
    // read is length-shaped rather than array-shaped. The assertion is erased; the `?.` / `?? 0`
    // that made it safe at runtime are the existing ones.
    const length = (value as { length?: number } | null | undefined)?.length ?? 0;

    if (rules.minNoOfFiles && length < rules.minNoOfFiles) {
      setFieldError(name, `Minimum ${rules.minNoOfFiles} files required`);
      return false;
    }

    if (rules.maxNoOfFiles && length > rules.maxNoOfFiles) {
      setFieldError(name, `Maximum ${rules.maxNoOfFiles} files allowed`);
      return false;
    }

    return true;
  };

  const validate = ({ name, value }: { name: string; value: unknown }) => {
    const fieldRules = rulesRef.current[name] || {};
    rulesRef.current = {
      ...rulesRef.current,
      [name]: {
        ...rulesRef.current[name],
        value,
      },
    };
    if (!checkRequired(name, value, fieldRules)) return false;
    if (!checkStringBounds(name, value, fieldRules)) return false;
    if (!checkNumberBounds(name, value, fieldRules)) return false;
    if (!checkFileCounts(name, value, fieldRules)) return false;

    clearFieldError(name);
    return true;
  };

  function validateAll() {
    let isValid = true;
    for (const name of Object.keys(rulesRef.current)) {
      // `name` came from `Object.keys` of this very object, so the entry exists; the assertion
      // only tells `noUncheckedIndexedAccess` that, and is erased at emit.
      const res = validate({ name, value: rulesRef.current[name]!.value });
      if (!res) {
        isValid = false;
      }
    }
    return isValid;
  }

  return { validate, error, updateRules, resetRules, validateAll };
};

export default useFormValidator;
